import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';

import { Box } from 'ui/box';
import { SEARCH_DEBOUNCE_MS } from 'ui/search-field/search-field.constants';
import {
  SearchClearAdornment,
  SearchClearButton,
  SearchFieldControl,
  SearchIconAdornment,
} from 'ui/search-field/search-field.styles';
import type { SearchFieldProps } from 'ui/search-field/search-field.types';

import Icons from 'assets/icons';

const SearchField = ({
  onSearch,
  placeholder = 'Search...',
  'aria-label': ariaLabel = 'Search',
  size = 'large',
  sx,
  'data-testid': dataTestId = 'search-input',
}: SearchFieldProps) => {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const onSearchRef = useRef(onSearch);

  useEffect(() => {
    onSearchRef.current = onSearch;
  });

  const pendingSearchRef = useRef<number | null>(null);

  const cancelPendingSearch = useCallback(() => {
    if (pendingSearchRef.current === null) return;

    window.clearTimeout(pendingSearchRef.current);
    pendingSearchRef.current = null;
  }, []);

  useEffect(() => cancelPendingSearch, [cancelPendingSearch]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextSearch = event.target.value.trim();

    setValue(event.target.value);
    cancelPendingSearch();
    pendingSearchRef.current = window.setTimeout(() => {
      pendingSearchRef.current = null;
      onSearchRef.current(nextSearch);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleClear = () => {
    cancelPendingSearch();
    setValue('');
    onSearchRef.current('');
    inputRef.current?.focus();
  };

  return (
    <Box role="search" sx={sx}>
      <SearchFieldControl
        fieldSize={size}
        size={size}
        fullWidth
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        inputRef={inputRef}
        inputProps={{ 'aria-label': ariaLabel, 'data-testid': dataTestId }}
        startAdornment={
          <SearchIconAdornment fieldSize={size} position="start">
            <Icons.Search aria-hidden="true" focusable="false" />
          </SearchIconAdornment>
        }
        endAdornment={
          !!value && (
            <SearchClearAdornment position="end">
              <SearchClearButton
                fieldSize={size}
                onClick={handleClear}
                aria-label="Clear search"
                data-testid="clear-search-icon"
              >
                <Icons.Close aria-hidden="true" focusable="false" />
              </SearchClearButton>
            </SearchClearAdornment>
          )
        }
      />
    </Box>
  );
};

export default SearchField;
